import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LogEventService } from '../shared/log-event.service';
import { LogEvent, LogEventStatus, LogEventType } from '../shared/log-event.model';
import { SortDirection } from '../../shared/sort-direction';
import { ClipboardService } from '../../shared/services/clipboard.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { LogEventSearchCriteria } from '../shared/log-event-search-criteria.model';
import { CarrierService } from '../../user-dashboard/shared/carrier.service';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { DataTableColumn } from '../../shared/data-table/data-table.component';
import { Option } from '../../shared/data-filters/data-filter-select/data-filter-select.component';
import { LoaderService } from '../../shared/services/loader.service';
import { finalize, take, tap} from 'rxjs/operators';
import { ModalService } from '../../shared/services/modal.service';
import * as moment from 'moment';
import { JsonEditorService } from '../../shared/services/json-editor.service';
import { LOCAL_STORAGE_KEYS } from '../../core/constants';
import {HELPER_POSITION} from '../../shared/data-filters/data-filters.component';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';

@Component({
  selector: 'app-log-event-list',
  templateUrl: './log-event-list.component.html',
  styleUrls: ['./log-event-list.component.scss']
})
export class LogEventListComponent implements OnInit {

  carriers: Carrier[]             = [];
  carriersOptions: Option[]       = [];
  events: string[]                = [];
  eventsOptions: Option[]         = [];
  selectedEventsOptions: Option[] = [];
  statusOptions: Option[]         = [];

  logEvents: LogEvent[] = [];

  filters: LogEventSearchCriteria = {};

  LogEventStatus = LogEventStatus;
  LogEventType   = LogEventType;

  searchOnNavigate: boolean;

  public HelperPosition = HELPER_POSITION;

  @ViewChild('typeColumn', { static: true })
  typeColumn: TemplateRef<any>;

  @ViewChild('eventColumn', { static: true })
  eventColumn: TemplateRef<any>;

  @ViewChild('sourceColumn', { static: true })
  sourceColumn: TemplateRef<any>;

  @ViewChild('userEmailColumn', { static: true })
  userEmailColumn: TemplateRef<any>;

  @ViewChild('vehicleColumn', { static: true })
  vehicleColumn: TemplateRef<any>;

  @ViewChild('carrierNameColumn', { static: true })
  carrierNameColumn: TemplateRef<any>;

  @ViewChild('loadUuidColumn', { static: true })
  loadUuidColumn: TemplateRef<any>;

  @ViewChild('loadIdColumn', { static: true })
  loadIdColumn: TemplateRef<any>;

  @ViewChild('loadNumberColumn', { static: true })
  loadNumberColumn: TemplateRef<any>;

  @ViewChild('stopIdColumn', { static: true })
  stopIdColumn: TemplateRef<any>;

  @ViewChild('shipmentNumberColumn', { static: true })
  shipmentNumberColumn: TemplateRef<any>;

  @ViewChild('locationRefColumn', { static: true })
  locationRefColumn: TemplateRef<any>;

  @ViewChild('createdAtColumn', { static: true })
  createdAtColumn: TemplateRef<any>;

  @ViewChild('statusColumn', { static: true })
  statusColumn: TemplateRef<any>;

  @ViewChild('contextualDataColumn', { static: true })
  contextualDataColumn: TemplateRef<any>;

  @ViewChild('contextualDataActionsColumn', { static: true })
  contextualDataActionsColumn: TemplateRef<any>;

  @ViewChild('resultBodyColumn', { static: true })
  resultBodyColumn: TemplateRef<any>;

  @ViewChild('resultBodyActionsColumn', { static: true })
  resultBodyActionsColumn: TemplateRef<any>;

  @ViewChild('consumerVersionColumn', { static: true })
  consumerVersionColumn: TemplateRef<any>;

  @ViewChild('deviceIdColumn', { static: true })
  deviceIdColumn: TemplateRef<any>;

  columns: DataTableColumn[] = [];
  columnsStrings: string[]   = []
  sortColumn: DataTableColumn;
  startDate: moment.Moment;
  endDate: moment.Moment;
  minDate: moment.Moment;
  maxDate: moment.Moment;

  loadPage = (page: number, pageSize: number, sortColumn: DataTableColumn, direction: SortDirection) =>
    this.logEventsService.search(page - 1, pageSize, sortColumn.name, direction, this.filters);

  constructor(
    private logEventsService: LogEventService,
    private carrierService: CarrierService,
    private clipboard: ClipboardService,
    private toastr: ToastrService,
    private loader: LoaderService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: ModalService,
    private jsonEditorService: JsonEditorService,
    public link: NavigationLinkService,
  ) {
    logEventsService.getEvents().subscribe(events => {
      this.events         = events;
      const eventsOptions = [{ value: undefined, label: this.translate.instant('log-events.events.NONE') }];

      events.forEach(t => eventsOptions.push({ value: t, label: this.translate.instant('log-events.events.' + t) }));

      this.eventsOptions = eventsOptions;

      if (this.filters.event && this.filters.event.length > 0) {
        this.selectedEventsOptions = eventsOptions.filter(o => this.filters.event.indexOf(o.value) !== -1);
      }

      this.statusOptions = [
        { label: this.translate.instant('log-events.list.filters.status.placeholder'), value: undefined },
        { label: LogEventStatus.SUCCEED, value: LogEventStatus.SUCCEED },
        { label: LogEventStatus.PARTIALLY_SUCCEED, value: LogEventStatus.PARTIALLY_SUCCEED },
        { label: LogEventStatus.FAILED, value: LogEventStatus.FAILED }
      ];
    });

    this.carrierService.getAllLight().subscribe(carriers => {
      this.carriers = carriers;

      const carriersOptions: Option[] = [{
        value: undefined,
        label: this.translate.instant('log-events.list.filters.carrier.placeholder')
      }];

      carriers.forEach(c => carriersOptions.push({ value: c.id, label: c.name }));

      this.carriersOptions = carriersOptions;
    });
  }

  ngOnInit() {
    const dateColumn: DataTableColumn = {
      name: 'createdAt',
      displayed: true,
      translationKey: 'log-events.list.table.created-at',
      template: this.createdAtColumn,
      sortable: true,
      exportable: true,
    };

    this.columns = [
      {
        name: 'type',
        displayed: false,
        translationKey: 'log-events.list.table.type',
        template: this.typeColumn,
        sortable: false,
        exportable: true,
      },
      {
        name: 'status',
        displayed: true,
        translationKey: 'log-events.list.table.status',
        template: this.statusColumn,
        sortable: false,
        exportable: true,
      },
      {
        name: 'event',
        displayed: true,
        translationKey: 'log-events.list.table.event',
        template: this.eventColumn,
        sortable: false,
        exportable: true,
      },
      {
        name: 'source',
        displayed: true,
        translationKey: 'log-events.list.table.source',
        template: this.sourceColumn,
        sortable: false,
        exportable: true,
      },
      {
        name: 'userEmail',
        displayed: true,
        translationKey: 'log-events.list.table.user-email',
        template: this.userEmailColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'vehicle',
        displayed: true,
        translationKey: 'log-events.list.table.vehicle',
        template: this.vehicleColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'carrierName',
        displayed: true,
        translationKey: 'log-events.list.table.carrier',
        template: this.carrierNameColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'loadId',
        displayed: false,
        translationKey: 'log-events.list.table.load-id',
        template: this.loadIdColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'loadUuid',
        displayed: false,
        translationKey: 'log-events.list.table.load-uuid',
        template: this.loadUuidColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'loadNumber',
        displayed: false,
        translationKey: 'log-events.list.table.load-number',
        template: this.loadNumberColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'stopId',
        displayed: false,
        translationKey: 'log-events.list.table.stop-id',
        template: this.stopIdColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'shipmentNumber',
        displayed: true,
        translationKey: 'log-events.list.table.shipment-number',
        template: this.shipmentNumberColumn,
        sortable: true,
        exportable: true,
      },
      {
        name: 'locationRef',
        displayed: true,
        translationKey: 'log-events.list.table.location-ref',
        template: this.locationRefColumn,
        sortable: true,
        exportable: true,
      },
      dateColumn,
      {
        name: 'contextualData',
        displayed: false,
        translationKey: 'log-events.list.table.contextual-data',
        template: this.contextualDataColumn,
        sortable: false,
        exportable: true,
      },
      {
        name: 'contextual_data_actions',
        displayed: true,
        translationKey: 'log-events.list.table.contextual-data-actions',
        template: this.contextualDataActionsColumn,
        sortable: false,
        exportable: false,
      },
      {
        name: 'consumerVersion',
        displayed: false,
        translationKey: 'log-events.list.table.consumer-version',
        template: this.consumerVersionColumn,
        sortable: false,
        exportable: true,
      },
      {
        name: 'resultBody',
        displayed: false,
        translationKey: 'log-events.list.table.result-body',
        template: this.resultBodyColumn,
        sortable: false,
        exportable: true,
      },
      {
        name: 'result_body_actions',
        displayed: true,
        translationKey: 'log-events.list.table.result-body-actions',
        template: this.resultBodyActionsColumn,
        sortable: false,
        exportable: false,
      },
      {
        name: 'deviceId',
        displayed: false,
        translationKey: 'log-events.list.table.device-id',
        template: this.deviceIdColumn,
        sortable: false,
        exportable: true,
      }
    ];

    this.sortColumn = dateColumn;

    this.route.queryParams
      .pipe(
        take(1),
        tap( params => this.setFiltersFromParams(params)),
        tap( params => this.searchOnNavigate = params.searchOnNavigate)
      ).subscribe();



    this.columnsStrings = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.LOG_EVENTS_COLUMNS));
    if (this.columnsStrings === null) {
      this.setColumnString();
    }
    this.columns.map((column: DataTableColumn) => {
      column.displayed = this.columnsStrings.indexOf(column.name) !== -1;
      return column;
    })
    this.updateRoute();
  }

  private setFiltersFromParams(params: any) {
    let eventsList: string[] = [];
    let loadIdList: string[] = [];
    let loadNumberList: string[] = [];
    let shipmentNumberList: string[] = [];
    let columnsList: string[] = [];

    if (Array.isArray(params.event)) {
      eventsList = params.event;
    } else if (params.event) {
      eventsList.push(params.event);
    }

    if (Array.isArray(params.loadId)) {
      loadIdList = params.loadId;
    } else if (params.loadId) {
      loadIdList.push(params.loadId);
    }

    if (Array.isArray(params.loadNumber)) {
      loadNumberList = params.loadNumber;
    } else if (params.loadNumber) {
      loadNumberList.push(params.loadNumber);
    }

    if (Array.isArray(params.shipmentNumber)) {
      shipmentNumberList = params.shipmentNumber;
    } else if (params.shipmentNumber) {
      shipmentNumberList.push(params.shipmentNumber);
    }

    if (Array.isArray(params.columns)) {
      columnsList = params.columns;
    } else if (params.columns) {
      columnsList.push(params.columns);
    }

    if (columnsList.length) {
      this.columns.map((column: DataTableColumn) => {
        column.displayed = columnsList.indexOf(column.name) !== -1;
        return column;
      })
    }

    this.filters.start_date = params.start_date ? moment(params.start_date).format('YYYY-MM-DD') : null;
    this.filters.end_date = params.end_date ? moment(params.end_date).format('YYYY-MM-DD') : null;

    if (this.filters.start_date && this.filters.start_date !== 'Invalid date') {
      this.startDate = moment(this.filters.start_date);
      this.maxDate = moment(this.filters.start_date).add(3, 'M');
    }
    if (this.filters.end_date && this.filters.end_date !== 'Invalid date') {
      const potentialEndDate = moment(this.filters.end_date);
      const isEndDateAfterMaxDate = this.maxDate != null && this.maxDate.isBefore(potentialEndDate);
      this.endDate = isEndDateAfterMaxDate ? moment(this.maxDate) : potentialEndDate
      this.minDate = moment(this.endDate).subtract(3, 'M');
    }

    this.filters.event = eventsList;
    this.filters.userEmail = params.userEmail;
    this.filters.vehicle = params.vehicle;
    this.filters.carrierId = params.carrierId;
    this.filters.status = params.status;
    this.filters.loadUuid = params.loadUuid;
    this.filters.loadIds = loadIdList;
    this.filters.loadNumbers = loadNumberList;
    this.filters.stopId = params.stopId;
    this.filters.shipmentNumbers = shipmentNumberList;
    this.filters.locationRef = params.locationRef;
    this.filters.consumerVersion = params.consumerVersion;
  }

  updateStartDateFilter() {
    if (this.startDate) {
      this.filters.start_date = this.startDate.utc(true).valueOf();
      this.maxDate = moment(this.filters.start_date).add(3, 'M');
    } else {
      this.filters.start_date = null;
      this.maxDate = null;
    }
  }

  updateEndDateFilter() {
    if (this.endDate) {
      this.filters.end_date = this.endDate.utc(true).valueOf();
      this.minDate = moment(this.filters.end_date).subtract(3, 'M');
    } else {
      this.filters.end_date = null;
      this.minDate = null;
    }
  }

  beforeReset() {
    this.startDate             = null;
    this.endDate               = null;
    this.maxDate = null;
    this.minDate = null;
    this.selectedEventsOptions = [];
  }

  onReset() {
    this.filters.loadIds         = [];
    this.filters.loadNumbers     = [];
    this.filters.shipmentNumbers = [];
    this.updateRoute();
  }

  copyJson(data: string) {
    if (this.clipboard.copy(data)) {
      this.toastr.success(this.translate.instant('copy.success'));
    } else {
      this.toastr.error(this.translate.instant('copy.error'));
    }
  }

  openContextualData(logEvent: LogEvent) {
    this.loader.startLoad();

    this.jsonEditorService.createJsonEditorDoc(`log-event-contextual-data-${logEvent.id}`, logEvent.contextualData)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(result => {
        if (result.ok) {
          window.open(`http://jsoneditoronline.org/?id=${result.id}`, '_blank');
        }
      });
  }

  openResult(logEvent: LogEvent) {
    this.loader.startLoad();

    this.jsonEditorService.createJsonEditorDoc(`log-event-result-${logEvent.id}`, logEvent.resultBody)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(result => {
        if (result.ok) {
          window.open(`http://jsoneditoronline.org/?id=${result.id}`, '_blank');
        }
      });
  }

  selectedEventsChanged() {
    this.filters.event = this.selectedEventsOptions.map(o => o.value);
  }

  selectedCarrierChanged(carrier: Carrier) {
    this.filters.carrierId = carrier ? carrier.id : null;
  }

  openConfirmExportLogEvents(content) {
    this.modalService.open({
      title: this.translate.instant('log-events.modals.export.title'),
      content,
      validateButton: {
        text: this.translate.instant('log-events.list.filters.export'),
        icon: 'fa fa-download'
      },
      closeButton: {
        text: this.translate.instant('log-events.modals.export.close'),
      },
      size: 'sm'
    }).subscribe(() => this.exportLogEvents());
  }

  exportLogEvents() {
    if (this.selectedColumns().length !== 0) {
      this.loader.startLoadAndCloseWhenComplete(
        this.logEventsService.exportLogEvents(this.filters, this.selectedColumns())
      ).subscribe();
    }
  }

  selectedColumns(): string[] {
    return this.columns.filter(column => column.displayed && column.exportable)
      .map(column => column.name);
  }

  pageLoaded() {
    this.updateRoute();
  }

  updateRoute() {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: {
          columns: this.columnsStrings,
          ...this.filters
        }
      });
  }

  setColumnString() {
    this.columnsStrings = this.columns
      .filter((column: DataTableColumn) => {
        return column.displayed
      })
      .map((column: DataTableColumn) => {
        return column.name
      });
  }

  columnSelected() {
    this.setColumnString();
    localStorage.setItem(LOCAL_STORAGE_KEYS.LOG_EVENTS_COLUMNS, JSON.stringify(this.columnsStrings));
    this.updateRoute();
  }

  public emptyDateFilter() {
    return this.filters.end_date == null || this.filters.start_date == null;
  }

  public emptyIdentityFilter() {
    return (this.filters.shipmentNumbers == null || this.filters.shipmentNumbers.length === 0)
      && (this.filters.loadIds == null || this.filters.loadIds.length === 0)
      && (this.filters.loadNumbers == null || this.filters.loadNumbers.length === 0);
  }

  public emptyNonIdentityFilter() {
    return (this.filters.locationRef == null || this.filters.locationRef.trim().length === 0)
      && (this.filters.vehicle == null || this.filters.vehicle.trim().length === 0)
      && (this.selectedEventsOptions == null || this.selectedEventsOptions.length === 0);
  }

  public disableSearch() {
    return this.emptyIdentityFilter()
      && !(!this.emptyDateFilter() && !this.emptyNonIdentityFilter())
  }
}
